import { toHex, utf8 } from './bytes';
import { blobAad, blobIdFor, blobIdForContent, contentHash, derivePathKey, seal } from './crypto';
import { sealManifestBody } from './manifest';
import { recordBetween, sealRecordOrNull } from './manifestRecord';
import { compatLevelOf, isFromANewerApp, withCreatedAt, withEditedAt } from './trailer';
import type { CloudManifest, ManifestEnvelope } from './manifest';
import type { CloudVault, VaultBackend } from './cloudVault';
import { VaultUnavailableError } from './cloudVault';
import { resolveId } from '../entity/catalog';
import { parseEntity } from '../entity/entity';

/**
 * Writing files back without holding the rest of the vault.
 *
 * The app's push is a full mirror: it diffs the cloud against a local folder
 * and deletes whatever the folder does not hold. A browser holding a handful
 * of files would erase everything else. But full-mirroring is a property of
 * that diff, not of the format — the manifest body already carries every
 * path and hash, so replacing a single entry is correct without ever seeing
 * another file's bytes. **This writer must never compute a removal set.**
 * Entries it did not touch are copied through untouched.
 *
 * As a grant actor it also cannot recompute two envelope fields: the
 * masterKey-wrapped `wrappedDataKey` and the custodian `accessAuthHash`.
 * Both are echoed verbatim, which the backend enforces — a mangled
 * `wrappedDataKey` would break every other device's bootstrap, and a changed
 * stamp is refused outright.
 */

const MAX_ATTEMPTS = 4;

/** Not a registered device — the browser holds no key of its own. The id is
 *  a label in the envelope and in the change fan-out, so a per-session value
 *  is enough and identifies nothing that outlives the tab. */
const DEVICE_ID = `web-${toHex(crypto.getRandomValues(new Uint8Array(4)))}`;

export class VaultWriteError extends Error {}

/**
 * A losing CAS is the NORMAL case here, not an error: the phone pushes
 * whenever it records anything. This is thrown only when the retries are
 * exhausted, and it matters more than a usual failure — see the note on the
 * blob window below.
 */
export class VaultWriteConflictError extends VaultWriteError {}

/** The vault will not take this batch as it stands, however often it is sent. */
export class VaultWriteRefusedError extends VaultWriteError {}

/** A file refused for being a newer app's, and the level it states: Infinity where it is one no
 *  build reads, which neither a newer page nor the app can write. */
export interface RefusedFile {
  path: string;
  level: number;
}

/**
 * A file in the batch was written by a newer version of the app, which this head would drop what
 * it carries from; nothing in the batch was written. [path] is the file the write asked to change,
 * the queue's own key, even where the write followed it to where it was renamed.
 */
export class VaultFileFromNewerAppError extends VaultWriteError {
  constructor(
    readonly path: string,
    readonly level: number,
  ) {
    super(refusalMessage([{ path, level }]));
  }
}

/** The longest a file's name runs in a refusal, which is read in the sync dot. */
const NAME_LENGTH = 20;

/**
 * What to tell the person once [files] were not saved because a newer app wrote them: the first by
 * name, the rest counted. A reload or the app is offered only where one of them states a level a
 * build could read.
 */
export function refusalMessage(files: readonly RefusedFile[]): string {
  const rest = files.length - 1;
  const who = whoOf(files.map((f) => f.path));
  if (files.some((f) => Number.isFinite(f.level))) {
    return `${who} not saved: a newer app wrote ${rest === 0 ? 'it' : 'them'}. Reload, or use the app.`;
  }
  return `${who} not saved: ${rest === 0 ? 'its file' : 'their files'} cannot be changed.`;
}

/** What to tell the person once changes to [paths] were not written because their files were gone
 *  ([FileWrite.skipIfGone]): the first by name, the rest counted. */
export function goneMessage(paths: readonly string[]): string {
  return `${whoOf(paths)} not saved: ${paths.length === 1 ? 'its file is' : 'their files are'} gone from the vault. Reload.`;
}

function whoOf(paths: readonly string[]): string {
  const base = paths[0]!.split('/').pop()!.replace(/\.md$/, '');
  const name = base.length > NAME_LENGTH ? `${base.slice(0, NAME_LENGTH - 1)}…` : base;
  const rest = paths.length - 1;
  return rest === 0 ? `"${name}"` : `"${name}" and ${rest} more`;
}

/** A change to one file, as a function of the file it is applied to. */
export type FileChange = (current: string) => string;

export interface FileWrite {
  path: string;
  /** The file as the page expects it once this write lands — what it shows meanwhile. */
  text: string;
  /**
   * The change itself, applied to the file as it stands when the write goes out. A device may
   * have re-saved the file since the page read it, and writing the page's copy over that re-save
   * would undo it. Absent for a write that sets the whole file, such as a new one.
   */
  change?: FileChange;
  /**
   * Awaited when the write goes out, once the vault has synced and before [change] is applied: a
   * change that reads other files besides its own reads them again here, as they stand then.
   */
  refresh?: (vault: CloudVault) => Promise<void>;
  /**
   * A change that never makes its file: where the vault holds it neither at [path] nor renamed when
   * the write goes out, nothing is written for it, rather than the page's text.
   */
  skipIfGone?: boolean;
  /** Called once the batch has gone out without this write, where [skipIfGone] found its file gone. */
  onSkipped?: () => void;
  /**
   * Where the file stands, with its text, when the vault holds it neither at [path] nor where the
   * catalog has its id: for a file known by more than its id, which a catalog that has not caught up
   * with a rename cannot follow.
   */
  findMoved?: (vault: CloudVault) => Promise<{ path: string; text: string } | null>;
  /**
   * Writes made as one act, such as a new arc and the nook that holds it, share a key. A file in it
   * that a newer app wrote is refused, and the others are let go with it rather than written
   * without it.
   */
  act?: string;
}

/** [write] as it lands now: its change applied to the file this sync left, or its text when it
 *  carries none or the vault no longer holds the file, or nothing for a change that never makes its
 *  file. A file renamed since the page read it is followed by its id to where the catalog now has
 *  it, or to where the write finds it ([FileWrite.findMoved]), rather than recreated under the old
 *  name. */
async function landing(vault: CloudVault, write: FileWrite): Promise<{ path: string; text: string } | null> {
  if (!write.change) return unlessNewer(write, { path: write.path, text: write.text });
  await write.refresh?.(vault);
  const current = await vault.readFile(write.path);
  if (current !== null) return unlessNewer(write, { path: write.path, text: write.change(current) });
  const moved = await movedTo(vault, write);
  if (moved) return unlessNewer(write, moved);
  return write.skipIfGone ? null : unlessNewer(write, { path: write.path, text: write.text });
}

/** A change keeps the trailer of the file it was made on, so a file a newer app wrote is caught
 *  in what would land: this head would drop what that app carries. */
function unlessNewer(write: FileWrite, landed: { path: string; text: string }): { path: string; text: string } {
  if (isFromANewerApp(landed.text)) throw new VaultFileFromNewerAppError(write.path, compatLevelOf(landed.text));
  return landed;
}

async function movedTo(vault: CloudVault, write: FileWrite): Promise<{ path: string; text: string } | null> {
  const found = (await byCatalog(vault, write)) ?? (await write.findMoved?.(vault)) ?? null;
  if (!found || found.path === write.path) return null;
  return { path: found.path, text: write.change!(found.text) };
}

async function byCatalog(vault: CloudVault, write: FileWrite): Promise<{ path: string; text: string } | null> {
  const id = parseEntity(write.path, write.text).frontMatter['id'];
  if (typeof id !== 'string') return null;
  const entry = await resolveId(vault, id);
  if (!entry || entry.path === write.path) return null;
  const current = await vault.readFile(entry.path);
  if (current === null || parseEntity(entry.path, current).frontMatter['id'] !== id) return null;
  return { path: entry.path, text: current };
}

export async function writeFile(
  vault: CloudVault,
  backend: VaultBackend,
  dataKey: Uint8Array,
  path: string,
  text: string,
): Promise<number> {
  return writeFiles(vault, backend, dataKey, [{ path, text }]);
}

/**
 * Several files under ONE manifest push.
 *
 * The manifest is the transaction boundary, so a batch is not just cheaper
 * than a loop — it is the only way a multi-file edit is atomic. Renumbering a
 * board column touches every arc in it; done one push at a time, a lost race
 * halfway through would leave the column partly renumbered with no way back.
 */
/** v2 blob ids are content-derived, so a content change mints a NEW id rather
 *  than moving a counter — the AAD version is a constant, and `pullOnce` keeps
 *  one uniform read path across both formats. */
const V2_BLOB_VERSION = 1;

export async function writeFiles(
  vault: CloudVault,
  backend: VaultBackend,
  dataKey: Uint8Array,
  writes: FileWrite[],
): Promise<number> {
  if (writes.length === 0) return vault.manifestVersion;
  const pathKey = derivePathKey(dataKey);

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    await vault.sync();
    const manifest = vault.currentManifest;
    const envelope = vault.currentEnvelope;
    if (!manifest || !envelope) {
      throw new VaultUnavailableError('no vault to write into');
    }

    // Writes FOLLOW the format the vault was read in. The web never cuts a
    // vault over on its own: the fleet moves when a device deliberately
    // upgrades, and everyone else — including this one — follows by reading.
    const format = vault.manifestFormat;
    const nextManifest: CloudManifest = {
      blobs: { ...manifest.blobs },
      files: { ...manifest.files },
    };
    const supersededBlobIds: string[] = [];

    // Seal everything before uploading anything, so the whole batch can
    // share one signed-url call. Those urls expire in five minutes for a
    // grant actor, which is ample for a column but not worth spending one
    // round trip per file on.
    const sealedBlobs: { blobId: string; bytes: Uint8Array }[] = [];
    const editedAtMs = vault.serverTimeMs;
    const landings = await Promise.all(writes.map((w) => landing(vault, w)));
    const skipped = writes.filter((_, i) => landings[i] === null);
    const resolved = landings.filter((w) => w !== null);
    if (resolved.length === 0) {
      for (const w of skipped) w.onSkipped?.();
      return vault.manifestVersion;
    }
    for (const { path, text } of resolved) {
      // A device that takes this edit dates it by the trailer's `e` rather than by the render the
      // edit patched, and mints a file this write makes as made then. Only from the backend's
      // clock: with none, the file goes as it was written.
      const previousBlobId = nextManifest.files[path];
      const dated = editedAtMs === null ? text : withEditedAt(previousBlobId ? text : withCreatedAt(text, editedAtMs), editedAtMs);
      const plaintext = utf8(dated);
      const hash = contentHash(plaintext);

      if (format === 'v2') {
        // Content-keyed: the id follows the bytes, so this can only ever write
        // a key nothing points at yet. A losing CAS leaves an orphan rather
        // than a blob the manifest can no longer open.
        const blobId = blobIdForContent(pathKey, hash);
        nextManifest.files[path] = blobId;
        if (previousBlobId && previousBlobId !== blobId) supersededBlobIds.push(previousBlobId);

        // Already in the vault at this exact content — another path holds it,
        // or this write is a no-op. Manifest-only edit, no bytes move.
        if (nextManifest.blobs[blobId]) continue;

        const bytes = seal(dataKey, blobAad(blobId, V2_BLOB_VERSION), plaintext);
        sealedBlobs.push({ blobId, bytes });
        nextManifest.blobs[blobId] = {
          version: V2_BLOB_VERSION,
          contentHash: hash,
          sizeEnc: bytes.length,
        };
        continue;
      }

      const blobId = previousBlobId ?? blobIdFor(pathKey, path);
      const nextVersion = (manifest.blobs[blobId]?.version ?? 0) + 1;

      // v1 only: the blob object is keyed by blobId alone — the version lives
      // in the AAD, not the path — so this upload REPLACES what readers at the
      // current version can open. Between here and a winning CAS the file is
      // briefly unreadable to everyone, and a lost CAS leaves it unreadable
      // for good. That window is why a lost CAS retries immediately rather
      // than reporting, why exhausting the retries is worth surfacing loudly,
      // and why v2 exists.
      const bytes = seal(dataKey, blobAad(blobId, nextVersion), plaintext);
      sealedBlobs.push({ blobId, bytes });

      nextManifest.blobs[blobId] = {
        version: nextVersion,
        contentHash: hash,
        sizeEnc: bytes.length,
      };
      nextManifest.files[path] = blobId;
    }

    // A blob the write pointed away from and nothing else references is
    // unreachable — the same rule the full push applies, scoped to the ids
    // this batch touched so a sparse writer never rules on paths it did not
    // read. The bytes stay in storage for the full push's sweep to reclaim.
    if (supersededBlobIds.length > 0) {
      const referenced = new Set(Object.values(nextManifest.files));
      for (const blobId of supersededBlobIds) {
        if (!referenced.has(blobId)) delete nextManifest.blobs[blobId];
      }
    }

    const nextVersion = envelope.manifestVersion + 1;
    const record = sealRecordOrNull(dataKey, recordBetween(manifest, nextManifest, nextVersion));
    // Behind a fenced snapshot an edit sends its record alone: the web never rewrites the snapshot,
    // and so never fences one.
    const recordOnly = envelope.snapshotVersion !== undefined && record !== null;
    // An unfenced body would take the fence off, which the backend refuses.
    if (envelope.snapshotVersion !== undefined && record === null) {
      throw new VaultWriteRefusedError(
        `this edit is too big to send as one record while the vault's snapshot is fenced (${writes.length} files)`,
      );
    }
    const bodyBlobId = recordOnly ? null : toHex(crypto.getRandomValues(new Uint8Array(32)));
    if (bodyBlobId !== null) {
      sealedBlobs.push({ blobId: bodyBlobId, bytes: sealManifestBody(dataKey, nextManifest, format) });
    }

    // A manifest-only edit behind the fence has nothing to upload, and the url call refuses an
    // empty list.
    if (sealedBlobs.length > 0) {
      const urls = await writeUrls(backend, sealedBlobs.map((b) => b.blobId));
      for (const { blobId, bytes } of sealedBlobs) {
        await put(urls[blobId], blobId, bytes);
      }
    }

    const nextEnvelope: ManifestEnvelope = {
      manifestVersion: nextVersion,
      deviceId: DEVICE_ID,
      updatedAtMs: Date.now(),
      wrappedDataKey: envelope.wrappedDataKey,
      ...(envelope.accessAuthHash !== undefined
        ? { accessAuthHash: envelope.accessAuthHash }
        : {}),
      ...(bodyBlobId !== null ? { bodyBlobId } : {}),
    };

    let result: { ok: boolean; currentVersion: number };
    try {
      result = await backend.call<{ ok: boolean; currentVersion: number }>('pushVaultManifest', {
        expectedVersion: envelope.manifestVersion,
        envelope: nextEnvelope,
        ...(record !== null ? { record } : {}),
      });
    } catch (error) {
      // A concurrent custodian push does NOT surface as a lost CAS. The
      // custodian re-seals wrappedDataKey on every push — `wrapDataKey` uses
      // a fresh nonce, so the same key yields different bytes each time —
      // and the backend compares that echo BEFORE it compares versions. So
      // the ordinary race reads as "a grant writer must echo wrappedDataKey
      // verbatim", which sounds like a broken client and is not one. Same
      // remedy as a lost CAS: read again, rebuild, retry.
      if (!isRetryablePush(error)) throw error;
      continue;
    }

    if (result.ok) {
      // The vault's snapshot is where this push left it: kept, or this push's body.
      const landed: ManifestEnvelope = recordOnly
        ? {
          ...nextEnvelope,
          ...(envelope.bodyBlobId !== undefined ? { bodyBlobId: envelope.bodyBlobId } : {}),
          snapshotVersion: envelope.snapshotVersion,
          ...(envelope.snapshotWrittenAtMs !== undefined ? { snapshotWrittenAtMs: envelope.snapshotWrittenAtMs } : {}),
        }
        : nextEnvelope;
      vault.adoptWrittenManifest(nextManifest, landed, format);
      // The body we replaced is now unreachable. Deleting exactly that one
      // blob is not a removal set — it is the object this push superseded —
      // and it is best effort: an orphan costs storage, a failed delete
      // costs nothing else.
      if (bodyBlobId !== null && envelope.bodyBlobId && envelope.bodyBlobId !== bodyBlobId) {
        await backend
          .call('deleteVaultBlobs', { blobIds: [envelope.bodyBlobId] })
          .catch(() => {});
      }
      for (const w of skipped) w.onSkipped?.();
      return nextEnvelope.manifestVersion;
    }
  }

  throw new VaultWriteConflictError(
    writes.length === 1
      ? `another device kept winning the race for ${writes[0]!.path}; nothing was saved`
      : `another device kept winning the race for ${writes.length} files; nothing was saved`,
  );
}

/** Every `failed-precondition` from the push is a statement about state we
 *  read a moment ago, so a fresh read is the answer to all of them. Anything
 *  else — denied, malformed, offline — is not ours to retry. */
function isRetryablePush(error: unknown): boolean {
  return (error as { status?: string } | null)?.status === 'failed-precondition';
}

async function writeUrls(
  backend: VaultBackend,
  blobIds: string[],
): Promise<Record<string, string>> {
  const { urls } = await backend.call<{ urls: Record<string, string> }>('getVaultUploadUrls', {
    blobIds,
    mode: 'write',
  });
  return urls;
}

async function put(url: string | undefined, blobId: string, bytes: Uint8Array): Promise<void> {
  if (!url) throw new VaultWriteError(`no write url for ${blobId}`);
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: bytes as BodyInit,
  });
  if (!response.ok) throw new VaultWriteError(`blob PUT ${response.status}`);
}

export { DEVICE_ID };
